import { isOnPrem } from '@/utils/buildProfile';
import { boundedErrorName } from '@/utils/logger/bounded-error-name';
import { logger } from '@/utils/logger/logger';
import type { CachedIdentityResponse } from 'tauri-plugin-auth0/bindings/CachedIdentityResponse';
import { isTauri } from '../utils';

/**
 * Offline-capable cached identity (APP-6186, primitive b).
 *
 * The persisted `user_id`/`region` snapshot, read with no network or
 * token-refresh call. `null` when the user has never completed an online login
 * on this device — offline recording is gated until they do (FR-003).
 */
export type CachedIdentity = CachedIdentityResponse;

/**
 * The invoke target for the cached-identity read.
 *
 * `auth_cached_identity_read` is registered in the auth0 Tauri *plugin*, so the
 * target carries the `plugin:auth0|` prefix (unlike the main-app
 * `offline_recording_*` commands which are bare).
 *
 * Exported so the capability lock in `identity.acl.test.ts` can compare THIS
 * string against the command the plugin registers and the permission the
 * window is granted, rather than restating the name and asserting the two
 * sides independently.
 */
export const CACHED_IDENTITY_READ_COMMAND = 'plugin:auth0|auth_cached_identity_read';

/**
 * The read currently in flight, shared by every concurrent caller.
 *
 * `useCanRecordOffline` is mounted by ~20 components at once — every sidebar
 * nav item, the session-list toolbar, the tab header, the session context menu
 * — and each mount fired its own IPC call for an answer they all wanted at the
 * same instant. They now await one call.
 *
 * Deliberately in-flight ONLY: the slot is cleared as soon as the read settles,
 * so a later mount still gets a fresh answer. That is what keeps this a pure
 * de-duplication rather than a cache — the identity changes at login/logout,
 * and a settled value held past its read would need an invalidation contract
 * tied to those events. Nothing observable changes here: every caller inside
 * the window would have issued an identical read against the same Rust state.
 */
let inFlightRead: Promise<CachedIdentity | null> | null = null;

async function invokeCachedIdentityRead(): Promise<CachedIdentity | null> {
  const { invoke } = await import('@tauri-apps/api/core');
  return invoke<CachedIdentity | null>(CACHED_IDENTITY_READ_COMMAND);
}

/**
 * Bridge for the offline-capable identity read. TS only reads; persistence
 * happens in Rust at login (FR-001a).
 */
export const cachedIdentityBridge = {
  /**
   * Read the cached identity with no network/token-refresh call. Returns
   * `null` when no identity is cached (FR-003) or outside the desktop runtime
   * (offline recording is desktop-only).
   *
   * Rejects when the invoke itself fails — a Tauri capability (ACL) denial for
   * the auth0 plugin command being the failure seen in production. Callers must
   * handle that: see `useCanRecordOffline` for the fail-closed policy.
   */
  read(): Promise<CachedIdentity | null> {
    // On-prem uses frontend Keycloak/OIDC and intentionally does not register
    // the native Auth0 plugin command. `isOnPrem` folds at build time, so this
    // costs cloud builds nothing at runtime.
    if (!isTauri() || isOnPrem) {
      return Promise.resolve(null);
    }
    if (inFlightRead === null) {
      // Rejections are shared too, then cleared with everything else, so a
      // denied read never poisons the next caller.
      inFlightRead = invokeCachedIdentityRead()
        .catch((error: unknown) => {
          // Reported HERE, at the one invoke, rather than in each caller's
          // rejection branch. `useCanRecordOffline` is mounted by ~20
          // components at once, so a caller-side report turned one denial into
          // ~20 identical lines, and another ~20 on every remount. This is the
          // point where the fault actually happened and where it happens once.
          //
          // Re-thrown, never swallowed: callers still see the rejection and
          // still have to answer it (see `useCanRecordOffline`, which fails
          // closed on it). The log is the report; it is not the handling.
          // The NAME only. This sink applies no redaction to free text and the
          // rejection comes from the keychain-backed auth0 read, whose Rust
          // side resolves the account — a Tauri IPC rejection routinely echoes
          // the invoked payload or the keychain entry name. Bounding it HERE is
          // what makes the consumers' own bounded catches true: this is the one
          // place the raw error was still reaching Datadog.
          logger.error(
            '[offline-capture] cached identity read failed; callers must treat this device as unable to capture offline',
            { errorName: boundedErrorName(error) }
          );
          throw error;
        })
        .finally(() => {
          inFlightRead = null;
        });
    }
    return inFlightRead;
  },
};
